import { ChronoUnit, DateTimeFormatter, LocalDate } from "@js-joda/core";
import { match } from "ts-pattern";
import {
  CaregiverComplianceItemMobile,
  CaregiverDocumentTypeGroupInfo,
  CaregiverDocumentTypeId,
  CaregiverDocumentUploadId,
  CaregiverUploadDocumentMobile,
  ComplianceFormQuestion,
  ComplianceFormQuestionOptionId,
  ComplianceStatus,
} from "../messages";
import { STRINGS } from "../strings";
import { TrainingCenterCaregiverBundle } from "../system/AppState/AppStateTrainingCenter";
import { translate } from "../translate";
import { arrayAt } from "./arrayUtils";
import { assertDefined } from "./assertUndefinedable";

export const compliantStatuses: ComplianceStatus[] = ["Compliant", "Resolved", "Not due yet"];
export const incompliantStatuses: ComplianceStatus[] = ["Not Compliant", "Missing"];

export interface ComplianceCardData {
  type: "issue" | "expiresSoon" | "dueSoon" | "compliant" | "pending";
  subTitle: string;
  item: CaregiverComplianceItemMobile;
}

export function getComplianceCardInfo(item: CaregiverComplianceItemMobile): ComplianceCardData {
  const localDateFormatter = DateTimeFormatter.ofPattern("MM/dd/yyyy");
  const status = getComplianceItemStatus(item);

  const subTitle = (() => {
    switch (status.type) {
      case "issue": {
        if (status.rejectReason !== null) {
          return `${translate(STRINGS.REJECTED)}: ${status.rejectReason}`;
        }

        if (status.expiredAt !== null) {
          return translate(STRINGS.EXPIRED_ON_DATE).replace(
            "%date%",
            status.expiredAt.format(localDateFormatter)
          );
        }

        return item.complianceStatus === "Missing"
          ? translate(STRINGS.ITEM_IS_MISSING)
          : translate(STRINGS.ITEM_IS_NOT_COMPLIANT);
      }

      case "expiresSoon": {
        return status.inDays === 1
          ? translate(STRINGS.WILL_EXPIRE_TOMORROW)
          : translate(STRINGS.WILL_EXPIRE_IN_DAYS).replace("%days%", status.inDays.toString());
      }

      case "dueSoon": {
        return status.inDays === 1
          ? translate(STRINGS.IS_DUE_TOMORROW)
          : translate(STRINGS.IS_DUE_IN_DAYS).replace("%days%", status.inDays.toString());
      }

      case "pending":
        return translate(STRINGS.PENDING_APPROVAL);

      case "compliant": {
        switch (status.expiryType.type) {
          case "date":
            return translate(STRINGS.EXPIRES_ON_DATE).replace(
              "%date%",
              status.expiryType.date.format(localDateFormatter)
            );

          case "days":
            return translate(STRINGS.WILL_EXPIRE_IN_DAYS).replace(
              "%days%",
              status.expiryType.days.toString()
            );

          case "never":
            return translate(STRINGS.DOESNT_EXPIRE);
        }
      }
    }
  })();

  return {
    type: status.type,
    subTitle: subTitle,
    item: item,
  };
}

export function getComplianceItemStatus(item: CaregiverComplianceItemMobile) {
  const now = LocalDate.now();
  const dateThreshold = now.plusDays(45);

  if (incompliantStatuses.includes(item.complianceStatus)) {
    return {
      type: "issue" as const,
      rejectReason: arrayAt(item.uploads, 0)?.rejectReason ?? null,
      expiredAt: item.expiryDate?.isBefore(now) === true ? item.expiryDate : null,
    };
  }

  if (item.complianceStatus === "Not due yet" && item.dueDate?.isBefore(dateThreshold) === true) {
    return {
      type: "dueSoon" as const,
      inDays: now.until(item.dueDate, ChronoUnit.DAYS),
    };
  }

  if (item.complianceStatus === "Pending Uploads") {
    return { type: "pending" as const };
  }

  if (item.expiryDate !== null) {
    const expiresInDays = now.until(item.expiryDate, ChronoUnit.DAYS);

    if (item.expiryDate.isBefore(dateThreshold) && expiresInDays >= 0) {
      return {
        item: item,
        type: "expiresSoon" as const,
        inDays: expiresInDays,
      };
    }
  }

  const latestExpiryDateUpload =
    item.expiryDate === null ? null : getLatestApprovedDocument(item.uploads) ?? null;

  if (latestExpiryDateUpload !== null && latestExpiryDateUpload.expiryDate !== null) {
    const latestUploadExpiresInDays = now.until(latestExpiryDateUpload.expiryDate, ChronoUnit.DAYS);

    if (
      latestExpiryDateUpload.expiryDate.isBefore(dateThreshold) &&
      latestUploadExpiresInDays >= 0
    ) {
      return {
        type: "expiresSoon" as const,
        inDays: latestUploadExpiresInDays,
      };
    }
  }

  return {
    type: "compliant" as const,
    expiryType: (() => {
      if (latestExpiryDateUpload && latestExpiryDateUpload.expiryDate !== null) {
        return {
          type: "date" as const,
          date: latestExpiryDateUpload.expiryDate,
          days: now.until(latestExpiryDateUpload.expiryDate, ChronoUnit.DAYS),
        };
      }

      if (item.expiryDate === null) return { type: "never" as const };

      return {
        type: "days" as const,
        days: now.until(item.expiryDate, ChronoUnit.DAYS),
      };
    })(),
  };
}

export function getInServiceComplianceCardInfo(bundle: TrainingCenterCaregiverBundle) {
  const status = getInServiceComplianceStatus(bundle);

  switch (status.type) {
    case "compliant":
      return {
        type: "compliant" as const,
        subTitle: translate(STRINGS.COMPLIANT),
        bundle: bundle,
      };

    case "lateInService":
      return {
        type: "overdue" as const,
        subTitle: translate(STRINGS.YOUR_IN_SERVICE_IS_OVER_DUE),
        bundle: bundle,
      };

    case "inServiceDueSoon":
      return {
        type: "dueSoon" as const,
        subTitle:
          status.inDays === 1
            ? translate(STRINGS.IS_DUE_TOMORROW)
            : translate(STRINGS.IS_DUE_IN_DAYS).replace("%days%", status.inDays.toString()),
        bundle: bundle,
      };
  }
}

export function getInServiceComplianceStatus(bundle: TrainingCenterCaregiverBundle) {
  if (bundle.completionCertificate !== null) {
    return { type: "compliant" as const };
  }

  const daysToComplete = LocalDate.now().until(bundle.completionDate, ChronoUnit.DAYS);

  if (daysToComplete > 14) {
    return { type: "compliant" as const };
  }

  if (daysToComplete < 0) {
    return {
      type: "lateInService" as const,
      inDays: Math.abs(daysToComplete),
    };
  }

  return {
    type: "inServiceDueSoon" as const,
    inDays: daysToComplete,
  };
}

export function groupUploadsByParents(uploads: CaregiverUploadDocumentMobile[]) {
  const groups = new Map<string | null, CaregiverUploadDocumentMobile[]>();

  uploads.sort(
    (a, b) => CaregiverDocumentUploadId.unwrap(b.id) - CaregiverDocumentUploadId.unwrap(a.id)
  );

  for (const upload of uploads) {
    if (upload.documentNamesTrail.length < 2) {
      groups.set(null, [...(groups.get(null) ?? []), upload]);
    } else {
      const key = upload.documentNamesTrail
        .slice(Math.max(upload.documentNamesTrail.length - 2, 1))
        .join(" - ");

      groups.set(key, [...(groups.get(key) ?? []), upload]);
    }
  }

  return [...groups.entries()].map(([parent, uploadedDocuments]) => ({
    parent,
    uploads: uploadedDocuments,
  }));
}

export function getDocumentTypeByURL(url: string): "image" | "pdf" {
  const parts = url.split("/");
  const fileName = parts[parts.length - 1];
  const fileNameParts = fileName.split(".");
  const extension = fileNameParts[fileNameParts.length - 1].split("?")[0];

  return extension.toLowerCase() === "pdf" ? "pdf" : "image";
}

export function mapItemWithDocumentWithNewExpiryDate(
  item: CaregiverComplianceItemMobile,
  documentId: CaregiverDocumentUploadId,
  expiryDate: LocalDate | null
) {
  return {
    ...item,
    uploads: item.uploads.map((document) => {
      if (document.id === documentId) {
        return {
          ...document,
          expiryDate,
        };
      }
      return document;
    }),
  };
}

export function mapItemWithoutDocument(
  item: CaregiverComplianceItemMobile,
  documentId: CaregiverDocumentUploadId
) {
  return {
    ...item,
    uploads: item.uploads.filter((document) => document.id !== documentId),
  };
}
export function getItemStatus(item: CaregiverComplianceItemMobile) {
  return match(item.complianceStatus)
    .with("Compliant", () => translate(STRINGS.COMPLIANT))
    .with("Resolved", () => translate(STRINGS.COMPLIANT))
    .with("Not due yet", () => translate(STRINGS.NOT_DUE_YET))
    .with("Not Compliant", () => translate(STRINGS.ITEM_IS_NOT_COMPLIANT))
    .with("Missing", () => translate(STRINGS.ITEM_IS_MISSING))
    .with("Pending Uploads", () => translate(STRINGS.PENDING_APPROVAL))
    .exhaustive();
}

export function getUploadButtonText(item: CaregiverComplianceItemMobile) {
  if (item.documentType.isFillable) {
    return translate(STRINGS.SUBMIT_FORM);
  } else if (
    item.documentType.children.length > 0 &&
    item.documentType.children.every((c) => c.isFillable)
  ) {
    return translate(STRINGS.SUBMIT_FORMS);
  } else if (item.uploads.length > 0) {
    return translate(STRINGS.UPLOAD_NEW_DOCUMENT);
  }
  return translate(STRINGS.UPLOAD_DOCUMENT);
}

export function getLatestApprovedDocument(
  documents: CaregiverUploadDocumentMobile[]
): CaregiverUploadDocumentMobile | undefined {
  let latestDocument: CaregiverUploadDocumentMobile | undefined;
  let latestExpiryDate: LocalDate | null = null;

  for (const document of documents) {
    if (document.status === "Approved" && document.expires) {
      const expiryDate = document.expiryDate;
      if (expiryDate && (latestExpiryDate === null || expiryDate.isAfter(latestExpiryDate))) {
        latestExpiryDate = expiryDate;
        latestDocument = document;
      }
    }
  }

  return latestDocument;
}

export type DocumentToUpload = {
  id: CaregiverDocumentTypeId;
  name: string;
  isPassport: boolean;
};

export function flattenDocumentTypeGroupInfo(
  group: CaregiverDocumentTypeGroupInfo
): CaregiverDocumentTypeGroupInfo[] {
  return [group, ...group.children.flatMap(flattenDocumentTypeGroupInfo)];
}

export function getI9DocumentsToUpload({
  questions,
  questionIdToDocumentTypeId,
  documentIdToUploadDocument,
}: {
  questions: ComplianceFormQuestion[];
  questionIdToDocumentTypeId: Map<ComplianceFormQuestionOptionId, CaregiverDocumentTypeId>;
  documentIdToUploadDocument: Map<CaregiverDocumentTypeId, DocumentToUpload>;
}) {
  const optionIdToBase64: Map<ComplianceFormQuestionOptionId, string> = new Map();

  for (let i = 0; i < questions.length; i++) {
    const question = questions[i];
    const prevQuestion = i > 0 ? questions[i - 1] : null;

    if (
      question.type === "document" &&
      question.value !== null &&
      prevQuestion?.type === "radio" &&
      prevQuestion.value !== null
    ) {
      optionIdToBase64.set(prevQuestion.value, question.value);
    }
  }

  return Array.from(optionIdToBase64.entries()).map(
    ([optionId, base64]): {
      document: DocumentToUpload;
      base64: string;
    } => {
      const documentTypeId = assertDefined(questionIdToDocumentTypeId.get(optionId));
      const document = assertDefined(documentIdToUploadDocument.get(documentTypeId));
      return {
        document,
        base64,
      };
    }
  );
}
